import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, query, orderBy, increment } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface BookCirculationProps {
  domain: string;
}

interface CirculationRecord {
  id: string;
  bookId: string;
  bookTitle: string;
  memberId: string;
  memberName: string;
  memberType: string;
  issueDate: string;
  dueDate: string;
  returnDate: string | null;
  status: 'issued' | 'returned' | 'overdue';
  fineAmount: number;
  finePaid: boolean;
  remarks: string;
}

interface BookOption {
  id: string;
  title: string;
  author: string;
  availableCopies: number;
}

interface MemberOption {
  id: string;
  name: string;
  memberId: string;
  type: string;
  className?: string;
  section?: string;
}

const BookCirculation: React.FC<BookCirculationProps> = ({ domain }) => {
  const [records, setRecords] = useState<CirculationRecord[]>([]);
  const [books, setBooks] = useState<BookOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('issued');
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [issueForm, setIssueForm] = useState({
    bookId: '',
    memberId: '',
    dueDate: '',
    remarks: '',
  });

  const FINE_PER_DAY = 2; // Rs 2 per day late fine

  // Load circulation records
  useEffect(() => {
    if (!domain) return;

    const circulationRef = collection(db, `school-data/${domain}/library-circulation`);
    const q = query(circulationRef, orderBy('issueDate', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: CirculationRecord[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const record: CirculationRecord = {
          id: docSnap.id,
          bookId: data.bookId || '',
          bookTitle: data.bookTitle || '',
          memberId: data.memberId || '',
          memberName: data.memberName || '',
          memberType: data.memberType || '',
          issueDate: data.issueDate || '',
          dueDate: data.dueDate || '',
          returnDate: data.returnDate || null,
          status: data.status || 'issued',
          fineAmount: data.fineAmount || 0,
          finePaid: data.finePaid || false,
          remarks: data.remarks || '',
        };

        // Auto-detect overdue
        if (record.status === 'issued' && new Date(record.dueDate) < new Date()) {
          record.status = 'overdue';
        }

        list.push(record);
      });
      setRecords(list);
      setLoading(false);
    }, (error) => {
      console.error('Error loading circulation:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [domain]);

  // Load books for selection
  useEffect(() => {
    if (!domain) return;
    const booksRef = collection(db, `school-data/${domain}/library-books`);
    const unsubscribe = onSnapshot(booksRef, (snapshot) => {
      const list: BookOption[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          title: data.title || '',
          author: data.author || '',
          availableCopies: data.availableCopies ?? 0,
        });
      });
      setBooks(list.sort((a, b) => a.title.localeCompare(b.title)));
    });
    return () => unsubscribe();
  }, [domain]);

  // Load members
  useEffect(() => {
    if (!domain) return;
    const membersRef = collection(db, `school-data/${domain}/library-members`);
    const unsubscribe = onSnapshot(membersRef, (snapshot) => {
      const list: MemberOption[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          name: data.name || '',
          memberId: data.memberId || '',
          type: data.type || 'Student',
          className: data.className || '',
          section: data.section || '',
        });
      });
      setMembers(list.sort((a, b) => a.name.localeCompare(b.name)));
    });
    return () => unsubscribe();
  }, [domain]);

  const handleIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueForm.bookId || !issueForm.memberId || !issueForm.dueDate) {
      alert('Please fill all required fields');
      return;
    }

    const selectedBook = books.find((b) => b.id === issueForm.bookId);
    const selectedMember = members.find((m) => m.id === issueForm.memberId);

    if (!selectedBook || !selectedMember) {
      alert('Invalid book or member selection');
      return;
    }

    if (selectedBook.availableCopies <= 0) {
      alert('No copies available for this book');
      return;
    }

    setSubmitting(true);
    try {
      // Create circulation record
      await addDoc(collection(db, `school-data/${domain}/library-circulation`), {
        bookId: issueForm.bookId,
        bookTitle: selectedBook.title,
        memberId: selectedMember.memberId,
        memberName: selectedMember.name,
        memberType: selectedMember.type,
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: issueForm.dueDate,
        returnDate: null,
        status: 'issued',
        fineAmount: 0,
        finePaid: false,
        remarks: issueForm.remarks,
        createdAt: new Date().toISOString(),
      });

      // Decrement available copies
      const bookRef = doc(db, `school-data/${domain}/library-books`, issueForm.bookId);
      await updateDoc(bookRef, { availableCopies: increment(-1) });

      setShowIssueModal(false);
      setIssueForm({ bookId: '', memberId: '', dueDate: '', remarks: '' });
    } catch (error: any) {
      console.error('Error issuing book:', error);
      alert(`Failed to issue book: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async (record: CirculationRecord) => {
    const today = new Date();
    const dueDate = new Date(record.dueDate);
    let fine = 0;

    if (today > dueDate) {
      const daysLate = Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
      fine = daysLate * FINE_PER_DAY;
    }

    const confirmMsg = fine > 0
      ? `Return this book? Late fee: Rs ${fine} (${Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))} days overdue)`
      : 'Confirm returning this book?';

    if (!confirm(confirmMsg)) return;

    try {
      // Update circulation record
      const recordRef = doc(db, `school-data/${domain}/library-circulation`, record.id);
      await updateDoc(recordRef, {
        returnDate: today.toISOString().split('T')[0],
        status: 'returned',
        fineAmount: fine,
        finePaid: fine === 0,
      });

      // Increment available copies
      const bookRef = doc(db, `school-data/${domain}/library-books`, record.bookId);
      await updateDoc(bookRef, { availableCopies: increment(1) });

      // If fine, create fine record
      if (fine > 0) {
        await addDoc(collection(db, `school-data/${domain}/library-fines`), {
          circulationId: record.id,
          bookTitle: record.bookTitle,
          memberId: record.memberId,
          memberName: record.memberName,
          memberType: record.memberType,
          fineAmount: fine,
          paid: false,
          reason: 'Late return',
          issuedOn: today.toISOString().split('T')[0],
          dueDate: record.dueDate,
          returnDate: today.toISOString().split('T')[0],
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error: any) {
      console.error('Error returning book:', error);
      alert(`Failed to return book: ${error.message}`);
    }
  };

  // Default due date: 14 days from today
  const getDefaultDueDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().split('T')[0];
  };

  const filtered = records.filter((r) => {
    const matchesSearch = !search ||
      r.bookTitle.toLowerCase().includes(search.toLowerCase()) ||
      r.memberName.toLowerCase().includes(search.toLowerCase()) ||
      r.memberId.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter ||
      (statusFilter === 'issued' && r.status === 'overdue');
    return matchesSearch && matchesStatus;
  });

  const issuedCount = records.filter((r) => r.status === 'issued' || r.status === 'overdue').length;
  const overdueCount = records.filter((r) => r.status === 'overdue').length;
  const returnedToday = records.filter((r) => r.status === 'returned' && r.returnDate === new Date().toISOString().split('T')[0]).length;

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2 text-muted">Loading circulation records...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-arrow-up-right fs-3 text-primary"></i>
              <h4 className="mb-0 mt-1">{issuedCount}</h4>
              <small className="text-muted">Currently Issued</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-exclamation-triangle fs-3 text-danger"></i>
              <h4 className="mb-0 mt-1">{overdueCount}</h4>
              <small className="text-muted">Overdue</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-arrow-down-left fs-3 text-success"></i>
              <h4 className="mb-0 mt-1">{returnedToday}</h4>
              <small className="text-muted">Returned Today</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-currency-rupee fs-3 text-warning"></i>
              <h4 className="mb-0 mt-1">{FINE_PER_DAY}</h4>
              <small className="text-muted">Fine/Day (Rs)</small>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <i className="bi bi-arrow-left-right me-2 text-primary-custom"></i>
            Book Circulation
          </h5>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowIssueModal(true); setIssueForm({ ...issueForm, dueDate: getDefaultDueDate() }); }}>
            <i className="bi bi-box-arrow-up-right me-2"></i>Issue Book
          </button>
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by book title, member name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="issued">Currently Issued</option>
                <option value="returned">Returned</option>
                <option value="all">All Records</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              No circulation records found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Book</th>
                    <th>Member</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Return Date</th>
                    <th>Status</th>
                    <th>Fine</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((record) => (
                    <tr key={record.id}>
                      <td><strong>{record.bookTitle}</strong></td>
                      <td>
                        {record.memberName}
                        <small className="text-muted d-block">{record.memberId} ({record.memberType})</small>
                      </td>
                      <td><small>{record.issueDate}</small></td>
                      <td>
                        <small className={record.status === 'overdue' ? 'text-danger fw-bold' : ''}>
                          {record.dueDate}
                        </small>
                      </td>
                      <td><small>{record.returnDate || '-'}</small></td>
                      <td>
                        <span className={`badge ${
                          record.status === 'returned' ? 'bg-success' :
                          record.status === 'overdue' ? 'bg-danger' : 'bg-warning text-dark'
                        }`}>
                          {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        {record.fineAmount > 0 ? (
                          <span className={`badge ${record.finePaid ? 'bg-success' : 'bg-danger'}`}>
                            Rs {record.fineAmount}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {(record.status === 'issued' || record.status === 'overdue') && (
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleReturn(record)}
                            title="Return Book"
                          >
                            <i className="bi bi-box-arrow-in-down me-1"></i>Return
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Issue Modal */}
      {showIssueModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-box-arrow-up-right me-2"></i>Issue Book
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowIssueModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleIssue}>
                  <div className="mb-3">
                    <label className="form-label">Select Book *</label>
                    <select
                      className="form-select"
                      value={issueForm.bookId}
                      onChange={(e) => setIssueForm({ ...issueForm, bookId: e.target.value })}
                      required
                    >
                      <option value="">-- Select Book --</option>
                      {books.filter((b) => b.availableCopies > 0).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.title} - {b.author} ({b.availableCopies} available)
                        </option>
                      ))}
                    </select>
                    {books.filter((b) => b.availableCopies > 0).length === 0 && (
                      <small className="text-danger">No books available. Add books to inventory first.</small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Select Member *</label>
                    <select
                      className="form-select"
                      value={issueForm.memberId}
                      onChange={(e) => setIssueForm({ ...issueForm, memberId: e.target.value })}
                      required
                    >
                      <option value="">-- Select Member --</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({m.memberId}) - {m.type}{m.className ? ` | Class ${m.className}${m.section ? `-${m.section}` : ''}` : ''}
                        </option>
                      ))}
                    </select>
                    {members.length === 0 && (
                      <small className="text-danger">No members found. Add members first.</small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Due Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={issueForm.dueDate}
                      onChange={(e) => setIssueForm({ ...issueForm, dueDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <input
                      type="text"
                      className="form-control"
                      value={issueForm.remarks}
                      onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })}
                      placeholder="Optional notes"
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Issuing...</>
                      ) : (
                        <><i className="bi bi-check-circle me-2"></i>Issue Book</>
                      )}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowIssueModal(false)}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookCirculation;
